import type { OverviewSummary, Service, AlertSeverity } from "@coremonit/types";
import { makeRng, int, pick } from "./rng";

const BOTTLENECK_KINDS = [
  { kind: "cpu-saturation",     metric: "cpu > 92%",           sev: "high"   as AlertSeverity },
  { kind: "memory-pressure",    metric: "mem > 88%",           sev: "high"   as AlertSeverity },
  { kind: "latency-spike",      metric: "p99 > 2s",            sev: "critical" as AlertSeverity },
  { kind: "error-spike",        metric: "5xx rate > 3%",       sev: "critical" as AlertSeverity },
  { kind: "queue-backlog",      metric: "kafka lag > 100k",    sev: "medium" as AlertSeverity },
  { kind: "restart-loop",       metric: "> 6 restarts / 1h",   sev: "high"   as AlertSeverity },
  { kind: "db-lock-wait",       metric: "lock wait > 800ms",   sev: "medium" as AlertSeverity },
  { kind: "cache-thrash",       metric: "hit rate < 60%",      sev: "low"    as AlertSeverity },
];

export function buildOverview(services: Service[], seed = 42): OverviewSummary {
  const rng = makeRng(seed);
  const totals = {
    services: services.length,
    healthy:  services.filter(s => s.status === "healthy").length,
    warning:  services.filter(s => s.status === "warning").length,
    critical: services.filter(s => s.status === "critical").length,
    unknown:  services.filter(s => s.status === "unknown").length,
  };
  const p50 = services.reduce((a, s) => a + s.latencyP50Ms, 0) / services.length;
  const p95 = services.reduce((a, s) => a + s.latencyP95Ms, 0) / services.length;
  const p99 = services.reduce((a, s) => a + s.latencyP99Ms, 0) / services.length;
  const throughput = services.reduce((a, s) => a + s.requestsPerMin, 0);
  const errRate = services.reduce((a, s) => a + s.errorRatePct, 0) / services.length;
  const availability = services.reduce((a, s) => a + s.availabilityPct, 0) / services.length;

  // Heatmap: rows = top N services, cols = 24 hours
  const heatmap: number[][] = services.slice(0, 12).map(() =>
    Array.from({ length: 24 }, () => Math.round(rng() * 100))
  );

  const top = services
    .filter(s => s.status !== "healthy")
    .sort((a, b) => (b.errorRatePct + b.latencyP95Ms / 100) - (a.errorRatePct + a.latencyP95Ms / 100))
    .slice(0, 6)
    .map((s) => {
      const b = pick(rng, BOTTLENECK_KINDS);
      return { serviceId: s.id, serviceName: s.name, kind: b.kind, metric: b.metric, severity: b.sev };
    });

  const summary =
    totals.critical > 0
      ? `${totals.critical} critical incident${totals.critical > 1 ? "s" : ""} active — top blast-radius appears to be ${top[0]?.serviceName ?? "payment-service"} (${top[0]?.kind ?? "latency"}). Investigate recent deployments and downstream calls.`
      : totals.warning > 0
      ? `${totals.warning} services degraded but SLOs still meeting target. Watch ${top[0]?.serviceName ?? "search-service"} — trending toward threshold.`
      : `All services healthy. Overall SLO burn rate is ${(errRate * 6).toFixed(1)}× lower than budget.`;

  return {
    totals,
    latency: { p50: Math.round(p50), p95: Math.round(p95), p99: Math.round(p99), avg: Math.round((p50 + p95) / 2) },
    availabilityPct: +availability.toFixed(3),
    throughputRpm: throughput,
    errorRatePct: +errRate.toFixed(2),
    deploymentSuccessPct: +(94 + rng() * 5).toFixed(1),
    crashLoops: services.filter(s => s.restarts24h > 6).length,
    restarts24h: services.reduce((a, s) => a + s.restarts24h, 0),
    oomKills24h: services.reduce((a, s) => a + s.oomKills24h, 0),
    saturation: {
      cpuPct:      int(rng, 42, 72),
      memPct:      int(rng, 55, 80),
      diskPct:     int(rng, 30, 65),
      networkMbps: int(rng, 120, 780),
    },
    topBottlenecks: top,
    aiSummary: summary,
    heatmap,
    updatedAt: new Date().toISOString(),
  };
}
